import { useState, type FormEvent } from "react";
import { useT } from "../../i18n/LanguageProvider";

/** The server's bound (8–128), restated so the person is told before the round trip. */
const MIN_LENGTH = 8;
const MAX_LENGTH = 128;

/**
 * G-136 — choose a password, twice. Shared by the invitation and the reset
 * page, which differ only in what they say around it and where they go next.
 */
export function NewPasswordForm({
  submitLabel,
  error,
  busy,
  onSubmit,
}: {
  submitLabel: string;
  error: string | null;
  busy: boolean;
  onSubmit: (password: string) => void;
}) {
  const { t } = useT();
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLocalError(null);
    if (password.length < MIN_LENGTH) {
      setLocalError(t("account.passwordTooShort"));
      return;
    }
    if (password !== repeat) {
      setLocalError(t("account.passwordsDiffer"));
      return;
    }
    onSubmit(password);
  };

  const shown = localError ?? error;
  return (
    <form className="account-password-form" onSubmit={submit} noValidate>
      {shown ? <div className="alert error">{shown}</div> : null}
      <div className="field">
        <label htmlFor="account-new-password">{t("account.newPassword")}</label>
        <input
          id="account-new-password"
          type="password"
          autoComplete="new-password"
          minLength={MIN_LENGTH}
          maxLength={MAX_LENGTH}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <small className="hint">{t("account.passwordHint")}</small>
      </div>
      <div className="field">
        <label htmlFor="account-repeat-password">{t("account.repeatPassword")}</label>
        <input
          id="account-repeat-password"
          type="password"
          autoComplete="new-password"
          maxLength={MAX_LENGTH}
          value={repeat}
          onChange={(e) => setRepeat(e.target.value)}
        />
      </div>
      <div className="button-row">
        <button className="button" type="submit" disabled={busy}>
          {busy ? t("account.saving") : submitLabel}
        </button>
      </div>
    </form>
  );
}
